/**
 * Nasadkali absorber — turg'un rejim.
 *
 * ALGORITM
 *   m (param yoki H/P), A = L/(m·G), H_OG = G/(K_y·a·S).
 *   design: yOut berilgan → N_OG (Colburn) → Z = H_OG·N_OG.
 *           yOut ≤ m·xIn yoki L/G ≤ (L/G)min → Z = Infinity, converged = false.
 *   rating: Z berilgan → N_OG = Z/H_OG → yOut (teskari Colburn). A ≤ 1 bo'lsa
 *           natija muvozanat (operatsion chiziq pinch) bilan cheklanadi — warning.
 *   Keyin xOut (balans), Kremser bosqichlar, gidravlika.
 *
 * MODDIY BALANS: G·(yIn − yOut) = L·(xOut − xIn) — mol/s, mol ulushlari (suyultirilgan).
 *
 * OGOHLANTIRISH KODLARI (i18n):
 *   absorber.noGas                   critical  G = 0
 *   absorber.noLiquid                critical  L = 0 — yutilish yo'q
 *   absorber.targetBelowEquilibrium  critical  design: yOut ≤ m·xIn — erishib bo'lmaydi
 *   absorber.targetAboveInlet        warning   design: yOut ≥ yIn — Z = 0
 *   absorber.belowMinimumLiquid      critical  design: L/G ≤ (L/G)min → Z = ∞
 *                                    warning   rating: A ≤ 1 → yOut muvozanat bilan cheklangan
 *   absorber.flooding                critical  u/u_flood > 0.8
 *   absorber.lowGasLoad              info      u/u_flood < 0.3 (nasadka to'liq ho'llanmasligi mumkin)
 *   absorber.highPressureDrop        warning   ΔP/Z > 1000 Pa/m
 *   property.*                       (properties/ dan) xossa oralig'idan chiqish
 */
import { getSubstance } from '../../properties';
import { warn, type SteadyResult, type Warning } from '../../types';
import { packedHydraulics } from './hydraulics';
import {
  absorptionFactor,
  kremserStages,
  minLiquidToGas,
  ntuOverallGas,
  outletFromNtu,
} from './kremser';
import type { AbsorberInputs, AbsorberOutputs, AbsorberParams } from './types';

const round3 = (v: number): number => Number(v.toFixed(3));

export function validateParams(p: AbsorberParams): void {
  if (p.mode !== 'design' && p.mode !== 'rating')
    throw new Error('absorber: mode must be "design" or "rating"');
  if (p.equilibriumSlope === undefined && p.henryConstant === undefined)
    throw new Error('absorber: equilibriumSlope or henryConstant is required');
  if (p.equilibriumSlope !== undefined && !(p.equilibriumSlope > 0))
    throw new Error('absorber: equilibriumSlope must be > 0');
  if (p.henryConstant !== undefined && !(p.henryConstant > 0))
    throw new Error('absorber: henryConstant must be > 0');
  if (!(p.kya > 0)) throw new Error('absorber: kya must be > 0');
  if (!(p.diameter > 0)) throw new Error('absorber: diameter must be > 0');
  if (!(p.specificArea > 0)) throw new Error('absorber: specificArea must be > 0');
  if (!(p.voidFraction > 0 && p.voidFraction < 1))
    throw new Error('absorber: voidFraction must be in (0, 1)');
  if (p.packingFactor !== undefined && !(p.packingFactor > 0))
    throw new Error('absorber: packingFactor must be > 0');
  if (p.mode === 'rating' && !(p.packingHeight !== undefined && p.packingHeight > 0))
    throw new Error('absorber: rating mode requires packingHeight > 0');
  getSubstance(p.carrierGas);
  getSubstance(p.solvent);
}

export function validateInputs(p: AbsorberParams, i: AbsorberInputs): void {
  if (!(i.gasFlow >= 0 && Number.isFinite(i.gasFlow)))
    throw new Error('absorber: gasFlow must be ≥ 0');
  if (!(i.liquidFlow >= 0 && Number.isFinite(i.liquidFlow)))
    throw new Error('absorber: liquidFlow must be ≥ 0');
  if (!(i.yIn > 0 && i.yIn < 1)) throw new Error('absorber: yIn must be in (0, 1)');
  if (!(i.xIn >= 0 && i.xIn < 1)) throw new Error('absorber: xIn must be in [0, 1)');
  if (!(i.temperature > 0)) throw new Error('absorber: temperature must be > 0 K');
  if (!(i.pressure > 0)) throw new Error('absorber: pressure must be > 0');
  if (p.mode === 'design' && !(i.yOut !== undefined && i.yOut >= 0 && i.yOut < 1))
    throw new Error('absorber: design mode requires yOut in [0, 1)');
}

export function equilibriumSlope(p: AbsorberParams, P: number): number {
  return p.equilibriumSlope ?? (p.henryConstant as number) / P;
}

export function solveSteady(
  p: AbsorberParams,
  inputs: AbsorberInputs,
): SteadyResult<AbsorberOutputs> {
  validateParams(p);
  validateInputs(p, inputs);
  const warnings: Warning[] = [];
  const { gasFlow: G, liquidFlow: L, yIn, xIn, temperature: T, pressure: P } = inputs;
  const m = equilibriumSlope(p, P);
  const S = (Math.PI * p.diameter * p.diameter) / 4;
  const yStar = m * xIn;

  const hyd = packedHydraulics(p, { G, L, T, P }, warnings);
  const H_OG = G > 0 ? G / (p.kya * S) : 0;

  let yOut: number;
  let Z: number;
  let N_OG: number;
  let converged = true;
  let message: string | undefined;

  if (G <= 0 || L <= 0) {
    warn(warnings, G <= 0 ? 'absorber.noGas' : 'absorber.noLiquid', 'critical');
    yOut = yIn;
    N_OG = 0;
    Z = p.mode === 'rating' ? (p.packingHeight as number) : 0;
  } else if (p.mode === 'design') {
    const target = inputs.yOut as number;
    if (target >= yIn) {
      warn(warnings, 'absorber.targetAboveInlet', 'warning', { yIn, yOut: target });
      yOut = yIn;
      N_OG = 0;
      Z = 0;
    } else if (target <= yStar) {
      warn(warnings, 'absorber.targetBelowEquilibrium', 'critical', { yOut: target, yStar });
      yOut = target;
      N_OG = Infinity;
      Z = Infinity;
      converged = false;
      message = 'absorber.targetBelowEquilibrium';
    } else {
      const lgMin = minLiquidToGas(yIn, target, xIn, m);
      const A = absorptionFactor(L, G, m);
      N_OG = ntuOverallGas(yIn, target, xIn, m, A);
      yOut = target;
      if (!(L / G > lgMin) || !Number.isFinite(N_OG)) {
        warn(warnings, 'absorber.belowMinimumLiquid', 'critical', {
          LoverG: round3(L / G),
          LminRatio: round3(lgMin),
        });
        N_OG = Infinity;
        Z = Infinity;
        converged = false;
        message = 'absorber.belowMinimumLiquid';
      } else {
        Z = H_OG * N_OG;
      }
    }
  } else {
    Z = p.packingHeight as number;
    N_OG = Z / H_OG;
    const A = absorptionFactor(L, G, m);
    yOut = outletFromNtu(yIn, xIn, m, A, N_OG);
    if (A <= 1)
      warn(warnings, 'absorber.belowMinimumLiquid', 'warning', { absorptionFactor: round3(A) });
  }

  const A = G > 0 && L > 0 ? absorptionFactor(L, G, m) : L > 0 ? Infinity : 0;
  const xOut = L > 0 ? xIn + (G * (yIn - yOut)) / L : xIn;
  const LminRatio = minLiquidToGas(yIn, yOut, xIn, m);
  const theoreticalStages = G > 0 && L > 0 ? kremserStages(yIn, yOut, xIn, m, A) : 0;

  if (G > 0) {
    if (hyd.floodingFraction > 0.8)
      warn(warnings, 'absorber.flooding', 'critical', { fraction: round3(hyd.floodingFraction) });
    else if (hyd.floodingFraction < 0.3)
      warn(warnings, 'absorber.lowGasLoad', 'info', { fraction: round3(hyd.floodingFraction) });
    if (hyd.pressureDropPerMeter > 1000)
      warn(warnings, 'absorber.highPressureDrop', 'warning', {
        dpPerMeter: Math.round(hyd.pressureDropPerMeter),
      });
  }

  const diagnostics =
    message !== undefined
      ? { converged, iterations: 0, residual: 0, message }
      : { converged, iterations: 0, residual: 0 };

  return {
    outputs: {
      yOut,
      xOut,
      Z,
      N_OG,
      H_OG,
      absorptionFactor: A,
      LminRatio,
      liquidMinFlow: G * LminRatio,
      LoverG: G > 0 ? L / G : Infinity,
      recovery: (yIn - yOut) / yIn,
      theoreticalStages,
      m,
      gasVelocity: hyd.gasVelocity,
      floodingVelocity: hyd.floodingVelocity,
      floodingFraction: hyd.floodingFraction,
      pressureDrop: Number.isFinite(Z) ? hyd.pressureDropPerMeter * Z : Infinity,
      pressureDropPerMeter: hyd.pressureDropPerMeter,
      gasDensity: hyd.gasDensity,
      liquidDensity: hyd.liquidDensity,
    },
    warnings,
    diagnostics,
  };
}
